import {
  ChangeEvent,
  useState,
  FocusEvent,
  MouseEvent as ReactMouseEvent,
} from "react"

import { Drawer, IconButton } from "@material-ui/core"
import { MediaConfigScope, MediaType } from "../types"
import { Tab, Tabs } from "components/Tabs"

import Channels from "./Channels"
import CloseIcon from "@material-ui/icons/Close"
import Fields from "./Fields"
import GeneralInfo from "./GeneralInfo"
import { MutateOptions } from "react-query"
import SubmediaTypes from "./SubmediaTypes"
import { css } from "@emotion/css"
import get from "lodash/fp/get"
import { useI18n } from "providers/I18n"

export type Props = {
  open: { isOpen: boolean; mediaType?: MediaType; id?: number }
  onClose: () => void
  colorOptions: string[]
  mediaType: MediaType
  patch: (
    variables: Partial<MediaType>,
    options?:
      | MutateOptions<MediaType, Partial<MediaType>, Error, unknown>
      | undefined,
  ) => Promise<MediaType>
  data?: MediaType[]
  handleChange: (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  handleColorSelection: (
    e: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    color?: string | undefined,
  ) => void
  configOwnerId?: string
  configScope?: MediaConfigScope
  updatedAt: number
}

const hasNoSubMedias = ["AGENCY"]
const hasNoFields = ["SUPPLIER", "AGENCY"]
const hasChannels = ["SUPPLIER"]

export function EditMediaTypeDrawer(props: Props) {
  const { t } = useI18n()
  const {
    open,
    onClose,
    mediaType,
    data,
    configScope = "GLOBAL",
    configOwnerId = "",
    patch,
  } = props

  const [activeTab, setActiveTab] = useState(() => "genral")

  const handleBlur = (
    evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement>,
  ) => {
    const { name, value, id } = evt.target
    !!data &&
      value !== get(id, data) &&
      patch({ id: mediaType?.id, [name]: mediaType[name as keyof MediaType] })
  }

  const mediaTypeSingle: Partial<MediaType> =
    !!data?.length && !!open?.id?.toString() ? data[open.id] : {}

  return (
    <>
      <Drawer
        variant="persistent"
        anchor="right"
        open={open.isOpen}
        classes={{
          paperAnchorRight: styles.drawer,
        }}
      >
        <div className={styles.container}>
          <div className={styles.header}>
            <h5 className="BodyBody-1BlackBold">
              {t("admin.mediaTypes.edit.mediaType")}
            </h5>
            <IconButton onClick={onClose} color="inherit">
              <CloseIcon />
            </IconButton>
          </div>
          <div>
            <Tabs value={activeTab} onTabChange={setActiveTab}>
              <Tab label={t("admin.mediaTypes.tabs.genral")} value="genral" />
              {!hasNoSubMedias.includes(configScope) ? (
                <Tab
                  label={t("admin.mediaTypes.tabs.submedia")}
                  disabled={!mediaType?.id}
                  value="submedia"
                />
              ) : null}
              {!hasNoFields.includes(configScope) ? (
                <Tab
                  label={t("admin.mediaTypes.tabs.fields")}
                  disabled={!mediaType?.id}
                  value="fields"
                />
              ) : null}
              {hasChannels.includes(configScope) ? (
                <Tab
                  label={t("admin.mediaTypes.tabs.vendorProducts")}
                  disabled={!mediaType?.id}
                  value="vendorProducts"
                />
              ) : null}
            </Tabs>
            <div>
              {activeTab === "genral" ? (
                <GeneralInfo
                  handleBlur={handleBlur}
                  {...props}
                  mediaType={mediaType}
                />
              ) : null}
            </div>
            <div>
              {activeTab === "submedia" ? (
                <SubmediaTypes mediaType={mediaTypeSingle} />
              ) : null}
            </div>
            <div>
              {activeTab === "fields" ? (
                <Fields
                  {...props}
                  configScope={configScope}
                  configOwnerId={configOwnerId}
                />
              ) : null}
            </div>
            <div>
              {activeTab === "vendorProducts" ? (
                <Channels
                  {...props}
                  configOwnerId={configOwnerId}
                  configScope={configScope}
                />
              ) : null}
            </div>
          </div>
        </div>
      </Drawer>
    </>
  )
}

const styles = {
  drawer: css`
    width: fit-content;
    display: flex;
    align-content: start;
    border: 0;
    box-shadow: 0 0 18px 0 rgba(0, 0, 0, 0.1);
    justify-content: space-between;
  `,
  container: css`
    padding: var(--spacing-l) var(--spacing-xxl-2);
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-xxl-2);
  `,
}
